import { useState, useEffect } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { useAuth } from '@/lib/auth';
import { destinationsAPI, statsAPI } from '@/lib/api';
import Navbar from '@/components/Navbar';
import Footer from '@/components/Footer';
import ScholarshipsShowcase from '@/components/ScholarshipsShowcase';
import UniversityInquiryDialog from '@/components/UniversityInquiryDialog';
import { Button } from '@/components/ui/button';
import { 
  GraduationCap, 
  MapPin, 
  Users, 
  Award, 
  ArrowRight, 
  CheckCircle,
  Check,
  Globe,
  Briefcase,
  TrendingUp,
  Building,
  Star,
  ChevronRight
} from 'lucide-react';

const LOGO_URL = "https://customer-assets.emergentagent.com/job_globalcampus-2/artifacts/g96w8w8c_HE%20Direct%20logo%202023.png";
const HERO_IMAGE = "https://images.unsplash.com/photo-1529431801612-df968fb9eb9c?crop=entropy&cs=srgb&fm=jpg&ixid=M3w3NTY2NzV8MHwxfHNlYXJjaHwzfHxtb2Rlcm4lMjB1bml2ZXJzaXR5JTIwYXJjaGl0ZWN0dXJlJTIwbmlnaHR8ZW58MHx8fHwxNzY3MjU5OTEzfDA&ixlib=rb-4.1.0&q=85";
const ICEF_LOGO = "https://customer-assets.emergentagent.com/job_globalcampus-2/artifacts/uybd5yuu_001bG000002DXv5QAG_badge.png";

const LandingPage = () => {
  const navigate = useNavigate();
  const { isAuthenticated, user } = useAuth();
  const [destinations, setDestinations] = useState([]);
  const [universityInquiryOpen, setUniversityInquiryOpen] = useState(false);

  useEffect(() => {
    const fetchData = async () => {
      try {
        const destRes = await destinationsAPI.getAll();
        setDestinations(destRes.data.slice(0, 6));
      } catch (e) {
        console.error('Error fetching data:', e);
      }
    };
    fetchData();
  }, []);

  const features = [
    {
      icon: GraduationCap,
      title: "Expert Guidance",
      description: "IAG qualified experts with years of experience in international admissions"
    },
    {
      icon: Globe,
      title: "Global Network",
      description: "Partnerships with 100+ universities across UK, USA, Canada, Australia & more"
    },
    {
      icon: Users,
      title: "Agent Network",
      description: "Partner with us and access competitive commissions with dedicated CRM support"
    },
    {
      icon: TrendingUp,
      title: "Track Record",
      description: "2,500+ students successfully placed in top universities worldwide"
    }
  ];

  const stats = [
    { value: "2,567+", label: "Students Counselled" },
    { value: "25+", label: "IAG Experts" },
    { value: "740+", label: "Success Stories" },
    { value: "106+", label: "Partner Universities" }
  ];

  return (
    <div className="min-h-screen bg-[#f8f9fa] dark:bg-slate-900 transition-colors">
      <Navbar />
      
      {/* Hero Section - Softer Colors */}
      <section className="relative min-h-[90vh] flex items-center pt-20">
        {/* Soft Gradient Background */}
        <div className="absolute inset-0 bg-gradient-to-br from-[#e9ecef] via-[#f1f3f5] to-[#f8f9fa] dark:from-slate-900 dark:via-slate-800 dark:to-slate-900 transition-colors duration-300"></div>
        <div className="absolute inset-0 opacity-5 dark:opacity-10">
          <img 
            src={HERO_IMAGE} 
            alt="University" 
            className="w-full h-full object-cover"
          />
        </div>
        
        <div className="relative max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-16">
          <div className="grid lg:grid-cols-2 gap-12 items-center">
            <div className="space-y-8">
              <div className="inline-flex items-center gap-2 px-4 py-2 bg-teal-100 dark:bg-teal-900/50 border border-teal-200 dark:border-teal-700 rounded-full">
                <Star className="w-4 h-4 text-teal-700 dark:text-teal-400" />
                <span className="text-teal-700 dark:text-teal-400 text-sm font-medium">ICEF Certified Agency</span>
              </div>
              
              <h1 className="text-4xl sm:text-5xl lg:text-6xl font-semibold text-gray-800 dark:text-white leading-tight">
                Education Without{' '}
                <span className="text-teal-700 dark:text-teal-400">Boundaries</span>
              </h1>
              
              <p className="text-base text-gray-500 dark:text-gray-400 max-w-xl leading-relaxed">
                Your trusted partner for studying abroad. We connect students and agents 
                with top universities across the UK, USA, Canada, Australia, and beyond.
              </p>
              
              <div className="flex flex-col sm:flex-row gap-4">
                {isAuthenticated ? (
                  <Button 
                    onClick={() => navigate(user?.role === 'student' ? '/student' : user?.role === 'agent' ? '/agent' : '/admin')}
                    className="bg-teal-600 text-white hover:bg-teal-700 font-semibold text-lg px-8 py-6 rounded-full"
                    data-testid="hero-dashboard-btn"
                  >
                    Go to Dashboard
                    <ArrowRight className="ml-2 w-5 h-5" />
                  </Button>
                ) : (
                  <>
                    <Button 
                      onClick={() => navigate('/register')}
                      className="bg-teal-600 text-white hover:bg-teal-700 font-semibold text-lg px-8 py-6 rounded-full"
                      data-testid="hero-student-btn"
                    >
                      I&apos;m a Student
                      <ArrowRight className="ml-2 w-5 h-5" />
                    </Button>
                    <Button 
                      onClick={() => navigate('/agent-register')}
                      variant="outline"
                      className="border-2 border-gray-300 dark:border-slate-600 hover:bg-gray-100 dark:hover:bg-slate-800 text-gray-700 dark:text-gray-200 font-semibold text-lg px-8 py-6 rounded-full"
                      data-testid="hero-agent-btn"
                    >
                      I&apos;m an Agent
                      <Briefcase className="ml-2 w-5 h-5" />
                    </Button>
                  </>
                )}
              </div>
              
              {/* Certification Badges */}
              <div className="flex items-center gap-6 pt-4">
                <a href="https://www.icef.com/agency/001bG000002DXv5QAG" target="_blank" rel="noopener noreferrer" className="opacity-90 hover:opacity-100 transition-opacity">
                  <img src={ICEF_LOGO} alt="ICEF Certified" className="h-14 w-auto" />
                </a>
                <div className="flex items-center gap-2 px-4 py-2 border border-gray-300 dark:border-slate-600 rounded-lg bg-white dark:bg-slate-800">
                  <span className="text-sm font-semibold text-gray-700 dark:text-gray-200">BRITISH COUNCIL</span>
                  <span className="text-xs text-teal-600 dark:text-teal-400 font-medium">Registered</span>
                </div>
              </div>
            </div>
            
            {/* Stats Cards */}
            <div className="hidden lg:grid grid-cols-2 gap-4">
              {stats.map((stat, idx) => (
                <div 
                  key={idx}
                  className="bg-white dark:bg-slate-800 p-6 rounded-2xl border border-gray-200 dark:border-slate-700 hover:border-teal-300 dark:hover:border-teal-600 hover:shadow-md transition-all duration-300"
                  style={{ animationDelay: `${idx * 100}ms` }}
                >
                  <div className="text-2xl font-semibold text-teal-700 dark:text-teal-400 mb-1">{stat.value}</div>
                  <div className="text-gray-500 dark:text-gray-400 text-sm">{stat.label}</div>
                </div>
              ))}
            </div>
          </div>
        </div>
      </section>

      {/* Features Section */}
      <section className="py-16 bg-[#e9ecef] dark:bg-slate-800 transition-colors duration-300">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-12">
            <h2 className="text-2xl sm:text-3xl font-semibold text-gray-800 dark:text-white mb-4">Why Choose H E Direct?</h2>
            <p className="text-gray-500 dark:text-gray-400 max-w-2xl mx-auto">
              We drive your future with personalised guidance and unmatched expertise
            </p>
          </div>
          
          <div className="grid md:grid-cols-2 lg:grid-cols-4 gap-5">
            {features.map((feature, idx) => (
              <div 
                key={idx}
                className="bg-white dark:bg-slate-900 p-6 rounded-2xl border border-gray-200 dark:border-slate-700 hover:border-teal-300 dark:hover:border-teal-600 hover:shadow-md transition-all duration-300 group"
                data-testid={`feature-card-${idx}`}
              >
                <div className="w-12 h-12 rounded-xl bg-teal-100 flex items-center justify-center mb-4 group-hover:bg-teal-200 transition-colors">
                  <feature.icon className="w-6 h-6 text-teal-700" />
                </div>
                <h3 className="text-base font-semibold text-gray-800 mb-2">{feature.title}</h3>
                <p className="text-gray-500 text-sm leading-relaxed">{feature.description}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Destinations Section */}
      <section className="py-16 bg-[#f8f9fa]">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="flex items-center justify-between mb-10">
            <div>
              <h2 className="text-2xl sm:text-3xl font-semibold text-gray-800 mb-2">Study Destinations</h2>
              <p className="text-gray-500 dark:text-gray-400">Explore top study destinations around the world</p>
            </div>
            <Link to="/destinations">
              <Button variant="ghost" className="text-teal-700 hover:text-teal-800 hover:bg-teal-50" data-testid="view-all-destinations">
                View All
                <ChevronRight className="ml-1 w-4 h-4" />
              </Button>
            </Link>
          </div>
          
          <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-6">
            {destinations.map((dest, idx) => (
              <Link 
                key={dest.id || idx}
                to={`/courses?country=${dest.country.split(' ')[0]}`}
                className="group relative h-72 rounded-2xl overflow-hidden cursor-pointer"
                data-testid={`destination-card-${idx}`}
              >
                <img 
                  src={dest.image_url} 
                  alt={dest.country}
                  className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-500"
                />
                <div className="absolute inset-0 bg-gradient-to-t from-gray-800/90 via-gray-700/40 to-gray-800/20"></div>
                
                {/* University Badge */}
                <div className="absolute top-4 left-4 flex items-center gap-1.5 text-teal-400 text-sm">
                  <MapPin className="w-4 h-4" />
                  <span className="font-medium">{dest.universities_count}+ Universities</span>
                </div>
                
                {/* Content */}
                <div className="absolute bottom-0 left-0 right-0 p-5">
                  <h3 className="text-xl font-semibold text-white mb-2">{dest.country}</h3>
                  <p className="text-white/90 text-sm line-clamp-2 leading-relaxed">{dest.description}</p>
                </div>
              </Link>
            ))}
          </div>
        </div>
      </section>

      {/* Agent Partnership Section */}
      <section className="py-16 bg-[#e9ecef]">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          {/* Section Header */}
          <div className="text-center mb-12">
            <p className="text-sm font-medium tracking-widest text-teal-600 uppercase mb-3">
              Partnership Opportunities
            </p>
            <h2 className="text-2xl sm:text-3xl font-semibold text-gray-800 mb-4">
              Become a Recruitment Partner
            </h2>
            <p className="text-gray-500 max-w-2xl mx-auto">
              Join our global network of education consultants and grow your business with industry-leading support.
            </p>
          </div>

          {/* Main Content Grid */}
          <div className="grid lg:grid-cols-5 gap-8">
            {/* Left Column - Image & Credentials */}
            <div className="lg:col-span-2">
              <div className="relative h-full min-h-[400px] rounded-2xl overflow-hidden">
                <img 
                  src="https://static.prod-images.emergentagent.com/jobs/059e3f4f-422c-431c-9cd2-949e2cce1086/images/60da3e1d0ec515da470c5decffeafff6f7a4146dcb004e12ae2463b4d2c91fd9.png"
                  alt="Professional Business Meeting"
                  className="absolute inset-0 w-full h-full object-cover"
                />
                <div className="absolute inset-0 bg-gradient-to-t from-gray-900/70 via-gray-800/30 to-transparent"></div>
                <div className="absolute bottom-0 left-0 right-0 p-6">
                  <p className="text-white/90 text-sm uppercase tracking-wider mb-3 font-medium">Accredited By</p>
                  <div className="flex items-center gap-4">
                    <div className="bg-white rounded-lg px-4 py-2">
                      <img src={ICEF_LOGO} alt="ICEF" className="h-8 object-contain" />
                    </div>
                    <div className="bg-white rounded-lg px-4 py-2">
                      <span className="text-gray-700 font-semibold text-sm">British Council</span>
                    </div>
                  </div>
                </div>
              </div>
            </div>

            {/* Right Column - Benefits & CTA */}
            <div className="lg:col-span-3 space-y-5">
              {/* Key Stats Row */}
              <div className="grid grid-cols-3 gap-4">
                <div className="bg-white rounded-2xl p-5 text-center border border-gray-200">
                  <p className="text-2xl font-semibold text-teal-700">106+</p>
                  <p className="text-gray-500 text-sm mt-1">Partner Universities</p>
                </div>
                <div className="bg-white rounded-2xl p-5 text-center border border-gray-200">
                  <p className="text-2xl font-semibold text-teal-700">9</p>
                  <p className="text-gray-500 text-sm mt-1">Countries</p>
                </div>
                <div className="bg-white rounded-2xl p-5 text-center border border-gray-200">
                  <p className="text-2xl font-semibold text-teal-700">2.5K+</p>
                  <p className="text-gray-500 text-sm mt-1">Students Placed</p>
                </div>
              </div>

              {/* Benefits List */}
              <div className="bg-white dark:bg-slate-800 rounded-2xl p-6 border-2 border-gray-200 dark:border-slate-700 transition-colors">
                <h3 className="text-base font-semibold text-gray-800 mb-5">Partner Benefits</h3>
                <div className="grid sm:grid-cols-2 gap-4">
                  {[
                    { title: 'Competitive Commission', desc: 'Tiered rates that grow with you' },
                    { title: 'Dedicated CRM System', desc: 'Track students & applications' },
                    { title: 'Marketing Support', desc: 'Co-branded materials provided' },
                    { title: 'Training & Development', desc: 'Regular workshops & updates' },
                    { title: 'Priority Processing', desc: 'Fast-track student applications' },
                    { title: 'Account Manager', desc: 'Dedicated point of contact' }
                  ].map((benefit, idx) => (
                    <div key={idx} className="flex items-start gap-3">
                      <div className="w-6 h-6 rounded-full bg-emerald-100 flex items-center justify-center flex-shrink-0 mt-0.5">
                        <Check className="w-3.5 h-3.5 text-emerald-600" />
                      </div>
                      <div>
                        <p className="text-gray-700 font-medium text-sm">{benefit.title}</p>
                        <p className="text-gray-500 text-xs">{benefit.desc}</p>
                      </div>
                    </div>
                  ))}
                </div>
              </div>

              {/* CTA */}
              <div className="flex flex-col sm:flex-row gap-4">
                <Button 
                  onClick={() => navigate('/agent-register')}
                  className="flex-1 bg-teal-600 hover:bg-teal-700 text-white font-semibold h-12 rounded-xl"
                  data-testid="cta-agent-btn"
                >
                  Apply to Become a Partner
                  <ArrowRight className="ml-2 w-4 h-4" />
                </Button>
                <Button 
                  variant="outline"
                  onClick={() => setUniversityInquiryOpen(true)}
                  className="flex-1 border-2 border-gray-300 text-gray-700 hover:bg-gray-100 h-12 rounded-xl"
                  data-testid="cta-contact-partnerships-btn"
                >
                  Contact Partnership Team
                </Button>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Services Section */}
      <section className="py-16 bg-[#f8f9fa]">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-12">
            <h2 className="text-2xl sm:text-3xl font-semibold text-gray-800 mb-4">Our Services</h2>
            <p className="text-gray-500 max-w-2xl mx-auto">
              Comprehensive support for students, universities, and partners
            </p>
          </div>
          
          <div className="grid md:grid-cols-3 gap-6">
            <div className="bg-white p-8 rounded-2xl text-center border border-gray-200 hover:border-teal-300 hover:shadow-md transition-all duration-300">
              <div className="w-14 h-14 rounded-2xl bg-teal-100 flex items-center justify-center mx-auto mb-6">
                <GraduationCap className="w-7 h-7 text-teal-700" />
              </div>
              <h3 className="text-lg font-semibold text-gray-800 mb-3">For Students</h3>
              <p className="text-gray-500 mb-6 leading-relaxed text-sm">
                Complete guidance from course selection to visa approval with personalized support throughout your journey.
              </p>
              <Button 
                onClick={() => navigate('/register')}
                variant="outline"
                className="border-2 border-teal-200 text-teal-700 hover:bg-teal-50 font-medium"
                data-testid="service-student-btn"
              >
                Start Your Journey
              </Button>
            </div>
            
            <div className="bg-white p-8 rounded-2xl text-center border border-gray-200 hover:border-teal-300 hover:shadow-md transition-all duration-300">
              <div className="w-14 h-14 rounded-2xl bg-teal-100 flex items-center justify-center mx-auto mb-6">
                <Building className="w-7 h-7 text-teal-700" />
              </div>
              <h3 className="text-lg font-semibold text-gray-800 mb-3">For Universities</h3>
              <p className="text-gray-500 mb-6 leading-relaxed text-sm">
                Market research and recruitment support to help you reach quality international students from Pakistan.
              </p>
              <Button 
                variant="outline"
                onClick={() => setUniversityInquiryOpen(true)}
                className="border-2 border-teal-200 text-teal-700 hover:bg-teal-50 font-medium"
                data-testid="service-university-btn"
              >
                Partner With Us
              </Button>
            </div>
            
            <div className="bg-white p-8 rounded-2xl text-center border border-gray-200 hover:border-teal-300 hover:shadow-md transition-all duration-300">
              <div className="w-14 h-14 rounded-2xl bg-teal-100 flex items-center justify-center mx-auto mb-6">
                <Users className="w-7 h-7 text-teal-700" />
              </div>
              <h3 className="text-lg font-semibold text-gray-800 mb-3">For Agents</h3>
              <p className="text-gray-500 mb-6 leading-relaxed text-sm">
                Join our agent network with competitive commissions, CRM access, and dedicated support for your business growth.
              </p>
              <Button 
                onClick={() => navigate('/agent-register')}
                variant="outline"
                className="border-2 border-teal-200 text-teal-700 hover:bg-teal-50 font-medium"
                data-testid="service-agent-btn"
              >
                Become a Partner
              </Button>
            </div>
          </div>
        </div>
      </section>

      <ScholarshipsShowcase />

      <Footer />
      <UniversityInquiryDialog
        open={universityInquiryOpen}
        onOpenChange={setUniversityInquiryOpen}
      />
    </div>
  );
};

export default LandingPage;
